import { useLocales } from '@/locales';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { useTranslate } from '@tolgee/react';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { filterPostsByPlatform } from '../postPerformance/helpers';
import { fetchCampaignData } from '@/redux/slices/analytics/campaignAnalytics';
import { lomavisShowToastr } from '@/utils/ui/toastr';
import IsLoadingIndicator from '@/components/isLoadingIndicator';
import { resolve } from '@/api/endPoints';
import axiosClient from '@/utils/axios';
import { CampaignType } from '@/redux/slices/lomavisCreator/lomavisCreatorConfig';
import { getLocationDetails } from '@/redux/slices/location/locationDetails';
import { LomavisTagListAndSelector1 } from '@/lomavisSharedUi/organisms/inputs/LomavisTagListAndSelector1';
import { Button } from 'react-bootstrap';
import { LomavisCard1 } from '@/lomavisSharedUi/atoms/cards/LomavisCard1';
import { LomavisList1 } from '@/lomavisSharedUi/atoms/lists/LomavisList1';
import {
  collateInteractionsByTimePerCampaign,
  collatePostsByTimePerCampaign,
  collatePostsByTypePerCampaign,
  formatCampaignDataForComponent,
  getCampaignAnalyticsStats,
  getCampaignDayCounts,
  getCampaignPostCounts,
  transformCampaignAnalyticsData
} from './helpers';
import { LomavisComparisionTableCard1 } from '@/lomavisSharedUi/organisms/cards/LomavisComparisionTableCard1';
import { LomavisBestPerformingPosts } from '@/lomavisSharedUi/templates/cards/ILomavisBestPerformingPosts';
import { LomavisBarChart3 } from '@/lomavisSharedUi/atoms/charts/LomavisBarChart3';
import { ILomavisMultipleHeatmap1 } from '@/lomavisSharedUi/molecules/charts/ILomavisMultipleHeatmap1';
import { LomavisUpgradeSubscriptionButton1 } from '@/lomavisSharedUi/atoms/links/LomavisUpgradeSubscriptionButton1';
import { LomavisH3 } from '@/lomavisSharedUi/atoms/typography/LomavisH3';

interface Props {
  platform?: string;
}
const CampaignAnalysis: React.FC<Props> = ({ platform }) => {
  const { t } = useTranslate();
  const dispatch = useDispatch<ThunkDispatch<any, any, AnyAction>>();
  const { locationslug } = useParams();

  const { loadingData } = useSelector((state: any) => state.campaignAnalytics);
  const data = useSelector(
    (state: any) => state.campaignAnalytics.campaignAnalyticsData
  );
  const locationDetails = useSelector((state: any) => state.locationDetails);

  const [selectedCampaigns, setSelectedCampaigns] = useState([]);

  const [selectedCampaignPosts, setSelectedCampaignPosts] = useState('all');

  const [loadingCampaign, setLoadingCampaign] = useState(false);
  const [allCampaigns, setAllCampaigns] = useState([]);

  const [posts, setPosts] = useState([]);

  const [isPostCampaignAllowed, setIsPostCampaignAllowed] = useState(true);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);

  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const getCampaignList = async () => {
    setLoadingCampaign(true);

    try {
      let url = resolve(
        'campaigns',
        {},
        { location__uuid: locationDetails?.uuid }
      );
      let allResults: CampaignType[] = [];

      while (url) {
        const res = await axiosClient.get(url);
        const data = res.data;

        allResults = [...allResults, ...data.results];

        // Move to the next page if available
        url = data.next;
      }

      setAllCampaigns(allResults);
    } catch (err: any) {
      console.error('Error fetching campaigns:', err);
      // setError("Failed to fetch campaigns");
    } finally {
      setLoadingCampaign(false);
    }
  };

  const fetchSelectedCampaignData = () => {
    if (locationslug) {
      setIsDataLoaded(true);
      dispatch(
        fetchCampaignData({
          locationSlug: locationslug,
          campaign_uuids: selectedCampaigns?.map((item) => item.uuid),
          onError: (err) => {
            lomavisShowToastr(t('campaign_analytics.err_gettingPost'), 'error');
            console.error('Error loading post data', err);
          }
        })
      );
    }
  };

  const getSubscriptionData = async () => {
    setIsLoadingSubscription(true);
    try {
      const url = resolve('current_subscription', {}, {});
      const { data } = await axiosClient.get(url);

      setIsPostCampaignAllowed(
        data?.social_media_management_subscription
          ?.post_campaigns_comparison_tool
      );
    } catch (err: any) {
      console.error('Error refreshing competitor:', err);
    } finally {
      setIsLoadingSubscription(false);
    }
  };

  const campaignAnalyticsData = useMemo(() => {
    if (!selectedCampaigns?.length) return [];

    const selectedCampaignUUIDs = selectedCampaigns.map((c) => c.uuid);

    return posts.filter((post) =>
      post.campaigns?.some((campaign) =>
        selectedCampaignUUIDs.includes(campaign.uuid)
      )
    );
  }, [selectedCampaigns, posts]);

  useEffect(() => {
    if (locationslug ?? '') {
      !platform &&
        dispatch(getLocationDetails(locationslug) as unknown as AnyAction);
      getSubscriptionData();
    }
  }, [dispatch, locationslug]);

  useEffect(() => {
    if (platform) {
      const filteredData = filterPostsByPlatform(data, platform);
      console.log('filtered posts', filteredData);

      setPosts(filteredData);
    } else {
      setPosts(data);
    }
  }, [platform, data]);

  useEffect(() => {
    if (locationDetails?.uuid) {
      !isLoadingSubscription && isPostCampaignAllowed && getCampaignList();
    }
  }, [locationDetails, isLoadingSubscription]);

  const options = [
    { value: 'all', label: t('campaignAnalytics.allCampaigns') },
    ...selectedCampaigns.map((c) => ({
      value: c.uuid,
      label: c.name
    }))
  ];
  const filteredCampaignPosts = useMemo(() => {
    if (selectedCampaignPosts === 'all' || !selectedCampaignPosts) {
      return campaignAnalyticsData;
    }

    return campaignAnalyticsData.filter((post) =>
      post.campaigns?.some((c) => c.uuid === selectedCampaignPosts)
    );
  }, [selectedCampaignPosts, campaignAnalyticsData]);

  useEffect(() => {
    if (selectedCampaigns?.length === 0) {
      setIsDataLoaded(false);
    }
  }, [selectedCampaigns]);

  const navBarHeight =
    document.getElementById('kt_app_header')?.clientHeight + 15 * 2 + 50;
  return (
    <div className="w-100 h-100">
      {isPostCampaignAllowed ? (
        <div className="w-100 d-flex flex-column gap-2">
          <div className="w-100">
            <div className="d-flex w-100 justify-content-end align-items-center">
              <div className="d-flex align-items-end flex-grow-1 w-auto justify-content-end mt-2">
                <LomavisTagListAndSelector1
                  availableTags={allCampaigns}
                  initialSelected={selectedCampaigns}
                  maxVisible={5}
                  maxAllowedTags={10}
                  // deleteTag={deleteCampaign}
                  allowCreate={false}
                  onTagsChange={(tags) => {
                    setSelectedCampaigns(tags);
                  }}
                  loadPost={() => fetchSelectedCampaignData()}
                  direction={'left'}
                  noTagsAvailableText={t(
                    'campaignAnalytics.noCampaignsAvailable'
                  )}
                  addTagsText={t('lomavisCreator.addCampaign')}
                  availableTagsText={t('campaignAnalytics.availableCampaigns')}
                  allTagsSelected={t('campaignAnalytics.allCampaignsSelected')}
                />
              </div>
            </div>
          </div>
          {selectedCampaigns?.length === 0 || !isDataLoaded ? (
            <div
              style={{
                height: `calc(100vh - ${navBarHeight}px)`
              }}
              className="w-100 d-flex  "
            >
              <div className="w-100 h-50 bg-white rounded p-2 d-flex flex-column gap-4 justify-content-center align-items-center">
                <i
                  style={{
                    fontSize: '3rem'
                  }}
                  className="fa-duotone fa-solid fa-chart-scatter text-primary fa-3x "
                ></i>
                <span className="fs-2 w-50 text-center">
                  <LomavisH3
                    title={t('campaignAnalytics.pleaseSelectCampaigns')}
                  />
                </span>
              </div>
            </div>
          ) : campaignAnalyticsData?.length === 0 && loadingData ? (
            <div
              style={{
                height: `calc(100vh - ${navBarHeight}px)`
              }}
              className="w-100 d-flex  "
            >
              <div className="w-100 h-50 bg-white rounded p-2 d-flex flex-column gap-4 justify-content-center align-items-center">
                <span className="fs-2 w-50 text-center">
                  <IsLoadingIndicator size={3} />
                </span>
              </div>
            </div>
          ) : campaignAnalyticsData?.length > 0 ? (
            <div className="w-100 h-100">
              <div className=" d-flex flex-column gap-2 ">
                <div className="row g-2">
                  <div className="col-12 col-md-6">
                    <LomavisCard1
                      title={'campaignAnalytics.postCount'}
                      showMoreInfo={true}
                      showSheetDownloadButton={true}
                      excelData={{
                        data: getCampaignPostCounts(
                          selectedCampaigns,
                          campaignAnalyticsData
                        )?.map((c, index) => ({
                          Campaign: c.name,
                          PostCount: c?.postCount
                          // you can change logic for highlight
                        }))
                      }}
                    >
                      <LomavisList1
                        placeholder={<i className="fa-regular fa-image"></i>}
                        data={getCampaignPostCounts(
                          selectedCampaigns,
                          campaignAnalyticsData
                        )?.map((c, index) => ({
                          image: c.logo_url || '',
                          title: c.name,
                          // subtitle: c.username,
                          value: c?.postCount

                          // you can change logic for highlight
                        }))}
                      />
                    </LomavisCard1>
                  </div>
                  <div className="col-12 col-md-6">
                    <LomavisCard1
                      title={'campaignAnalytics.dayCount'}
                      showMoreInfo={true}
                      showSheetDownloadButton={true}
                      excelData={{
                        data: getCampaignDayCounts(
                          selectedCampaigns,
                          campaignAnalyticsData
                        )?.map((c, index) => ({
                          Campaign: c.name,
                          DayCount: c?.dayCount
                          // you can change logic for highlight
                        }))
                      }}
                    >
                      <LomavisList1
                        placeholder={<i className="fa-regular fa-image"></i>}
                        data={getCampaignDayCounts(
                          selectedCampaigns,
                          campaignAnalyticsData
                        )?.map((c, index) => ({
                          image: c.logo_url || '',
                          title: c.name,
                          // subtitle: c.username,
                          value: c?.dayCount

                          // you can change logic for highlight
                        }))}
                      />
                    </LomavisCard1>
                  </div>
                </div>

                {/* //*statrt the remaining analytics here */}
                <div className="row g-2">
                  {[
                    {
                      key: 'impressions',
                      label: t('campaignAnalytics.impressions')
                    },
                    { key: 'reach', label: t('campaignAnalytics.reach') },
                    {
                      key: 'reactions',
                      label: t('campaignAnalytics.reactions')
                    },
                    { key: 'comments', label: t('campaignAnalytics.comments') },
                    { key: 'shares', label: t('campaignAnalytics.shares') },
                    {
                      key: 'engagement',
                      label: t('campaignAnalytics.engagement')
                    }
                  ].map(({ key, label }, idx) => {
                    const isEngagement = key === 'engagement';
                    const campaignStats = getCampaignAnalyticsStats(
                      selectedCampaigns,
                      campaignAnalyticsData
                    );

                    return (
                      <div className="col-12 col-md-6" key={key}>
                        <LomavisCard1
                          title={label}
                          showMoreInfo={true}
                          showSheetDownloadButton={true}
                          excelData={{
                            data: campaignStats.map((c) => ({
                              Campaign: c.name,
                              [label]: isEngagement
                                ? `${c[key as keyof typeof c] ?? 0}%`
                                : c[key as keyof typeof c]
                            }))
                          }}
                        >
                          <LomavisList1
                            placeholder={
                              <i className="fa-regular fa-image"></i>
                            }
                            data={campaignStats.map((c) => ({
                              image: c.logo_url || '',
                              title: c.name,
                              value: isEngagement
                                ? `${c[key as keyof typeof c] ?? 0}%`
                                : c[key as keyof typeof c] ?? 0
                            }))}
                          />
                        </LomavisCard1>
                      </div>
                    );
                  })}
                </div>

                <div className="w-100 h-100">
                  <LomavisComparisionTableCard1
                    title={t('campaignAnalytics.comparative_table')}
                    showMoreInfo={true}
                    enablePreview={true}
                    data={transformCampaignAnalyticsData(
                      selectedCampaigns,
                      campaignAnalyticsData,
                      t
                    )}
                  />
                </div>
                <div className={''}>
                  <LomavisBestPerformingPosts
                    title={t('campaignAnalytics.performance_by_metric.title')}
                    itemsToShow={3}
                    data={
                      formatCampaignDataForComponent(
                        filteredCampaignPosts,
                        selectedCampaigns,
                        selectedCampaignPosts,
                        t
                      ).data
                    }
                    sortOptions={
                      formatCampaignDataForComponent(
                        filteredCampaignPosts,
                        selectedCampaigns,
                        selectedCampaignPosts,
                        t
                      ).sortOptions
                    }
                    showProfileHead
                    customFilterOptions={options}
                    selectCustomFilter={(item) => {
                      setSelectedCampaignPosts(item);
                    }}
                    currentCustomFilter={selectedCampaignPosts}
                  />
                </div>

                <div>
                  <LomavisCard1
                    title={'lomavisCompetitorAnalysis.competitor_post_types'}
                    showMoreInfo={false}
                    className="h-500px"
                  >
                    <LomavisBarChart3
                      data={collatePostsByTypePerCampaign(
                        campaignAnalyticsData
                      )}
                      showAllXAxisLabels={true}
                    />
                  </LomavisCard1>
                </div>

                <div>
                  <LomavisCard1
                    title={'lomavisCompetitorAnalysis.post_time_heat_map'}
                    showMoreInfo={false}
                  >
                    <ILomavisMultipleHeatmap1
                      competitorsData={collatePostsByTimePerCampaign(
                        campaignAnalyticsData,
                        t
                      )}
                    />
                  </LomavisCard1>
                </div>

                <div>
                  <LomavisCard1
                    title={
                      'lomavisCompetitorAnalysis.interaction_time_heat_map'
                    }
                  >
                    <ILomavisMultipleHeatmap1
                      competitorsData={collateInteractionsByTimePerCampaign(
                        campaignAnalyticsData
                      )}
                      metric={t('interactions')}
                    />
                  </LomavisCard1>
                </div>
              </div>
            </div>
          ) : (
            <div
              style={{
                height: `calc(100vh - ${navBarHeight}px)`
              }}
              className="w-100 d-flex  "
            >
              <div className="w-100 h-50 bg-white rounded p-2 d-flex flex-column gap-4 justify-content-center align-items-center">
                <span className="fs-3 w-50 text-center">
                  <LomavisH3 title={t('campaignAnalytics.noData')} />
                </span>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div
          style={{
            height: `calc(100vh - ${navBarHeight}px)`
          }}
          className="w-100 d-flex  "
        >
          <div className="w-100 h-50 bg-white rounded p-2 d-flex flex-column gap-4 justify-content-center align-items-center">
            <span className="fs-3 w-50 text-center">
              {t('campaignAnalytics.notAvailable')}
            </span>
            <div className="d-flex gap-3 justify-content-center">
              {' '}
              <LomavisUpgradeSubscriptionButton1 />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignAnalysis;
